import L, { Circle, CustomSector, Marker } from 'leaflet';
import { isNumber, max } from 'lodash';
import {
  tmkIsNotSensor,
  tmkIsSensor,
  tmkIsWeapon,
  tmkKarakteristikUnnamed1,
  tmkKarakteristikUnnamed4,
} from 'src/app/helpers/binaries/preset-queries.binary';
import { MapViewState } from 'src/enums/map.enum';
import { MapVisualSettings } from 'src/types/components/map.type';
// import { MapViewState } from 'src/enums/map.enum';
// import { MapUnit } from 'src/types/components/unit.type';
import { Konum, MultiKonum, Polygon } from 'src/types/mixed.type';
import { TmkKalemiOzellikleriResultType } from 'src/types/services/hymotsc/tmk-kalemi-ozellikleri.type';
import { HLAUnit, MapUnit, HlaMapUnit } from 'src/types/unit.type';
import { OverlappedMarker } from './OverlappedUnits/overlapped-units.config';
import { TaskInformation } from 'src/types/redux/odTask.redux.type';
// import { InputMode } from '../MapInput/config';

export const mapDefaultZoomLevel = 2;

export interface MapPropsType {
  mapOptions?: L.MapOptions;
  mapViewState?: MapViewState;
  mapInputProps?: {
    initialData?: Konum | Polygon | MultiKonum;
    // inputMode?: InputMode;
    onInput?: (value: any) => void;
    displayOnly?: boolean;
  };
  unitsSelected: MapUnit[] | HlaMapUnit[];
  removeUnitMarkersOnMap?: number[];
  localTasks?: TaskInformation[];
  isTaskDropdownOpen?: boolean;
}

export interface RulerPopupArgs {
  distance: string;
  bearing: string;
  position: {
    x: number;
    y: number;
  };
  duration: number;
}

export type OverlappedIconAttributesType = {
  id: number;
  icon: L.DivIcon;
};

export type OverlappedUnitsClusterArgs = {
  event: L.LeafletMouseEvent;
  markers: OverlappedClusterMarker[];
};

export type OverlappedClusterMarker = {
  id: number;
  unit: MapUnit;
  marker: L.Marker;
  icon: L.DivIcon;
};

export type OverlappedUnitsPopupArgs = {
  markers: OverlappedMarker[];
  mouseEvent: L.LeafletMouseEvent;
};

export const mapCorners = {
  southWest: L.latLng(-85, -250),
  northEast: L.latLng(85, 250),
};

export const mapBounds = L.latLngBounds(mapCorners.southWest, mapCorners.northEast);

export const minimapConfig: L.TileLayerOptions = {
  minZoom: 0,
  maxZoom: 13,
};

export const mapBoundaries = {
  southWest: L.latLng(-85, -250),
  northEast: L.latLng(85, 250),
};

export const defaultWeight = 1;
export const highlightedWeight = 2;

export const filledStyle = (color: string): L.PathOptions => {
  return {
    weight: highlightedWeight,
    fillColor: color,
    fillOpacity: 0.9,
    color: '#59d4f3',
  };
};

export const highlightedStyle = (): L.PathOptions => {
  return {
    weight: highlightedWeight,
    fillColor: '#16254b',
    fillOpacity: 2,
    color: '#ffffff',
  };
};

export const defaultStyle = (isHighlighted: boolean = false): L.PathOptions => {
  if (isHighlighted) {
    return {
      weight: highlightedWeight,
      fillColor: 'var(--ete-primary-green-color)',

      fillOpacity: 1,
      color: '#ffffff',
    };
  }
  return {
    weight: defaultWeight,
    fillColor: '#999999',
    fillOpacity: 1,
    color: '#ffffff',
  };
};

export type TRangeProps = {
  innerRadius: number;
  radius: number;
};

export type TWeaponRangeProps = {
  innerRadius: number;
  radius: number;
};

export type TSenseSensorCollection = {
  innerCircle: Circle;
  konikler: CustomSector[];
};

export type TDetectionAngleType = {
  angle: number;
  range: number;
  innerRange: number;
};

export type TSenseRangeProps = {
  identificationRadius: number;
  detectionRadius: number;
  detectionAngle: TDetectionAngleType[];
};

export const defaultVisualSettings: MapVisualSettings = {
  displayNametags: true,
  displaySpeed: true,
  displayDirection: true,
  displayCasualityStatus: true,
  maxVisibleAgents: 10000,
  showDms: false,
  zoomToUnit: true,
  maritimeSpeedUnit: false,
  isShowAltitudeOrDepthAsMeter: false,
};

export const generalConfig = {
  agentsDisplayMethod:
    window.AGENTS_DISPLAY_METHOD &&
    !window.AGENTS_DISPLAY_METHOD.startsWith('{{') &&
    isNumber(Number(window.AGENTS_DISPLAY_METHOD))
      ? Number(window.AGENTS_DISPLAY_METHOD)
      : 0,
};

export const getSenseInfo = (system: TmkKalemiOzellikleriResultType) => {
  const maxIdentificationRanges: number[] = [];
  const maxDetectionRanges: number[] = [];
  const detectionAngles: TDetectionAngleType[] = [];

  detectionAngles.push({
    angle: system.tmkKalemiAlgilamaInsaParametreleri.param2,
    range: system.tespitMenzili * 1000,
    innerRange: system.teshisMenzili * 1000,
  });

  if (system.teshisMenzili !== undefined) {
    maxIdentificationRanges.push(system.teshisMenzili * 1000);
  }

  if (system.tespitMenzili !== undefined) {
    maxDetectionRanges.push(system.tespitMenzili * 1000);
  }

  return { maxIdentificationRanges, maxDetectionRanges, detectionAngles };
};

export const getWeaponRanges = (system: TmkKalemiOzellikleriResultType) => {
  const maxRanges: number[] = [];
  const minRanges: number[] = [];

  if (tmkIsWeapon(system)) {
    if (system.muhimmatIndeks?.default.vurmaOlasiligiParametreleri.param5) {
      minRanges.push(system.muhimmatIndeks.default.vurmaOlasiligiParametreleri.param5);
    }

    if (system.muhimmatIndeks?.default.vurmaOlasiligiParametreleri.param8) {
      maxRanges.push(system.muhimmatIndeks.default.vurmaOlasiligiParametreleri.param8);
    }

    system.muhimmatIndeks?.alternatifler.forEach(({ muhimmat }) => {
      if (muhimmat.vurmaOlasiligiParametreleri) {
        if (muhimmat.vurmaOlasiligiParametreleri.param5) {
          minRanges.push(muhimmat.vurmaOlasiligiParametreleri.param5);
        }
        maxRanges.push(muhimmat.vurmaOlasiligiParametreleri.param8);
      }
    });
  }

  return [minRanges, maxRanges];
};
